import React, { useState, useEffect } from 'react';
import { useDomain } from '../contexts/DomainContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import AISearchVisibility from '../components/AISearchVisibility';
import BrandImpactAnalysis from '../components/BrandImpactAnalysis';
import BrandSentimentAnalysis from '../components/BrandSentimentAnalysis';
import NarrativeAnalysis from '../components/NarrativeAnalysis';
import SearchQuestionsAnalysis from '../components/SearchQuestionsAnalysis';
import PromptIntelligence from '../components/PromptIntelligence';
import StrategicActionPlan from '../components/StrategicActionPlan';
import ContentStrategy from '../components/ContentStrategy';
import {
  CpuChipIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  SparklesIcon,
  ShieldCheckIcon,
  FaceSmileIcon
} from '@heroicons/react/24/outline';
import FAQSection from '../components/FAQSection';

const AISearchVisibilityPage = () => {
    const { selectedDomain } = useDomain();

  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [snapshotData, setSnapshotData] = useState(null);
  const [brandImpactData, setBrandImpactData] = useState(null);
  const [brandImpactLoading, setBrandImpactLoading] = useState(false);
  const [brandSentimentData, setBrandSentimentData] = useState(null);
  const [brandSentimentLoading, setBrandSentimentLoading] = useState(false);
  const [narrativeData, setNarrativeData] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [searchQuestionsData, setSearchQuestionsData] = useState(null);
  const [searchQuestionsLoading, setSearchQuestionsLoading] = useState(false);
  const [promptIntelligenceData, setPromptIntelligenceData] = useState(null);
  const [promptIntelligenceLoading, setPromptIntelligenceLoading] = useState(false);
  const [strategicPlanData, setStrategicPlanData] = useState(null);
  const [strategicPlanLoading, setStrategicPlanLoading] = useState(false);
  const [contentStrategyData, setContentStrategyData] = useState(null);
  const [contentStrategyLoading, setContentStrategyLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedDomains, setSavedDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [userPlan, setUserPlan] = useState('free');
  const [domainLimit, setDomainLimit] = useState(1);
  const [domainCount, setDomainCount] = useState(0);

  
  // Sync with global domain context
  useEffect(() => {
    if (selectedDomain) {
      setDomain(selectedDomain);
    }
  }, [selectedDomain]);

  // Auto-load saved data when domain is set and we have saved domains
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);
  useEffect(() => {
    if (domain && !autoLoaded && !snapshotData && !loading && savedDomains.length > 0) {
      const isExisting = savedDomains.some(d => d.domain.toLowerCase() === domain.replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase());
      if (isExisting) {
        setAutoLoaded(true);
      fetchRefreshStatus(domain);
        loadSavedData(domain);
        fetchRefreshStatus(domain);
      }
    }
  }, [domain, savedDomains, autoLoaded, snapshotData, loading]);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await fetch('/api/domains/', {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        const data = await res.json();
        if (data.domains) setSavedDomains(data.domains);
            if (data.plan) setUserPlan(data.plan);
            if (data.limit) setDomainLimit(data.limit);
            if (data.count !== undefined) setDomainCount(data.count);
      } catch (e) { console.error('Failed to load domains'); }
      setDomainsLoading(false);
    };
    fetchDomains();
  }, []);
  const [activeSection, setActiveSection] = useState('snapshot');
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('token');
  const [refreshBlocked, setRefreshBlocked] = useState(false);
  const [nextRefreshDate, setNextRefreshDate] = useState(null);

  const checkRefreshStatus = async (dom) => {
    try {
      const res = await fetch(`/api/analysis/saved-report/${encodeURIComponent(dom)}/visibility-snapshot`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.found && !data.canRefresh) {
        setRefreshBlocked(true);
        setNextRefreshDate(data.nextRefreshAt);
        return false;
      }
    } catch (e) {}
    setRefreshBlocked(false);
    return true;
  };

  // Auto-load saved data for a domain from the database
  const loadSavedData = async (dom) => {
    if (!dom) return;
    const normalizedDomain = dom.replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
    const token = getToken();
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      // Load visibility-snapshot first
      const snapRes = await fetch(`/api/analysis/saved-report/${encodeURIComponent(normalizedDomain)}/visibility-snapshot`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const snapData = await snapRes.json();
      if (snapData.found && snapData.data) {
        const parsedSnap = typeof snapData.data === 'string' ? JSON.parse(snapData.data) : snapData.data; setSnapshotData(parsedSnap);
        setActiveSection('snapshot');
        if (!snapData.canRefresh) {
          setRefreshBlocked(true);
          setNextRefreshDate(snapData.nextRefreshAt);
        }

        // Load sub-sections in parallel (non-blocking)
        const sections = ['brand-impact', 'brand-sentiment', 'narrative-analysis', 'search-questions', 'prompt-intelligence', 'strategic-action-plan', 'content-strategy'];
        const setters = {
          'brand-impact': (d) => setBrandImpactData(d),
          'brand-sentiment': (d) => setBrandSentimentData(d),
          'narrative-analysis': (d) => setNarrativeData(d),
          'search-questions': (d) => setSearchQuestionsData(d),
          'prompt-intelligence': (d) => setPromptIntelligenceData(d),
          'strategic-action-plan': (d) => setStrategicPlanData(d),
          'content-strategy': (d) => setContentStrategyData(d)
        };

        sections.forEach(async (section) => {
          try {
            const res = await fetch(`/api/analysis/saved-report/${encodeURIComponent(normalizedDomain)}/${section}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.found && data.data) {
              const parsedSectionData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data; setters[section](parsedSectionData);
            }
          } catch (e) {
            // Sub-section not saved yet, that's ok
          }
        });
      }
    } catch (e) {
      console.error('Failed to load saved data:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefreshStatus = async (dom) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/analysis/refresh-status/${dom}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRefreshStatus(data);
        if (data.plan) setUserPlan(data.plan);
      }
    } catch (e) {
      console.error('Failed to fetch refresh status:', e);
    }
  };

  const saveReport = async (dom, reportType, reportData) => {
    try {
      await fetch('/api/analysis/save-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ domain: dom, reportType, data: reportData })
      });
    } catch (e) { console.error('Failed to save report'); }
  };


  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!domain) return;
    setAutoLoaded(true); // Prevent auto-load from re-triggering
    const normalizedDomain = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
    const isExistingDomain = savedDomains.some(d => d.domain.toLowerCase() === normalizedDomain);
    if (!isExistingDomain && domainCount >= domainLimit) {
      setError(`Domain limit reached. Your ${userPlan} plan allows ${domainLimit} domain(s). Please upgrade your plan to add more domains.`);
      return;
    }
    if (!isExistingDomain) {
      try {
        const addRes = await fetch('/api/domains/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({ domain: normalizedDomain })
        });
        const addData = await addRes.json();
        if (!addData.error) {
          setDomainCount(prev => prev + 1);
          setSavedDomains(prev => [...prev, { domain: normalizedDomain }]);
        }
      } catch (e) { console.error('Failed to auto-add domain'); }
    }
    const canRun = await checkRefreshStatus(domain);
    if (!canRun) return;
    setLoading(true);
    setError(null);
    setScanData(null);
    setSnapshotData(null);
    setBrandImpactData(null);
    setBrandSentimentData(null);
      setNarrativeData(null);
      setSearchQuestionsData(null);
      setPromptIntelligenceData(null);
      setStrategicPlanData(null);
      setContentStrategyData(null);
    setActiveSection('snapshot');
    try {
      const url = domain.startsWith('http') ? domain : `https://${domain}`;
      const response = await fetch('/api/analysis/visibility-snapshot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ url })
      });
      const result = await response.json();
      if (result.ok) {
        setSnapshotData(result.snapshot);
            saveReport(domain, "visibility-snapshot", result.snapshot);
      } else {
        setError(result.error || 'Analysis failed');
      }
    } catch (err) {
      setError('Failed to connect to analysis service');
    } finally {
      setLoading(false);
    }
  };

  const loadBrandImpact = async () => {
    if (brandImpactData) return;
    setBrandImpactLoading(true);
    try {
      const url = domain.startsWith('http') ? domain : `https://${domain}`;
      const response = await fetch('/api/analysis/brand-impact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ url })
      });
      const result = await response.json();
      if (result.ok) {
        setBrandImpactData(result.impact);
      }
    } catch (err) {
      console.error('Brand impact error:', err);
    } finally {
      setBrandImpactLoading(false);
    }
  };

  const loadBrandSentiment = async () => {
    if (brandSentimentData) return;
    setBrandSentimentLoading(true);
    try {
      const url = domain.startsWith('http') ? domain : `https://${domain}`;
      const response = await fetch('/api/analysis/brand-sentiment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ url })
      });
      const result = await response.json();
      if (result.ok) {
        setBrandSentimentData(result.sentiment);
      }
    } catch (err) {
      console.error('Brand sentiment error:', err);
    } finally {
      setBrandSentimentLoading(false);
    }
  };

  const loadNarrativeAnalysis = async () => {
    if (narrativeData) return;
    setNarrativeLoading(true);
    try {
      const response = await fetch('/api/analysis/narrative-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ domain })
      });
      const result = await response.json();
      setNarrativeData(result);
    } catch (err) {
      console.error('Narrative analysis error:', err);
    }
    setNarrativeLoading(false);
  };

  const loadSearchQuestions = async () => {
    if (searchQuestionsData) return;
    setSearchQuestionsLoading(true);
    try {
      const response = await fetch('/api/analysis/search-questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ domain })
      });
      const result = await response.json();
      setSearchQuestionsData(result);
    } catch (err) {
      console.error('Search questions error:', err);
    }
    setSearchQuestionsLoading(false);
  };

  const loadPromptIntelligence = async () => {
    if (promptIntelligenceData) { setActiveSection('promptIntelligence'); return; }
    setPromptIntelligenceLoading(true);
    setActiveSection('promptIntelligence');
    try {
      const response = await fetch('/api/analysis/prompt-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ domain })
      });
      if (!response.ok) throw new Error('Failed');
      const result = await response.json();
      setPromptIntelligenceData(result);
    } catch (err) { console.error('Prompt intelligence error:', err); }
    finally { setPromptIntelligenceLoading(false); }
  };

  const loadStrategicPlan = async () => {
    if (strategicPlanData) { setActiveSection('strategicPlan'); return; }
    setStrategicPlanLoading(true);
    setActiveSection('strategicPlan');
    try {
      const response = await fetch('/api/analysis/strategic-action-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ domain })
      });
      if (!response.ok) throw new Error('Failed');
      const result = await response.json();
      setStrategicPlanData(result);
    } catch (err) { console.error('Strategic plan error:', err); }
    finally { setStrategicPlanLoading(false); }
  };

  const loadContentStrategy = async () => {
    if (contentStrategyData) { setActiveSection('contentStrategy'); return; }
    setContentStrategyLoading(true);
    setActiveSection('contentStrategy');
    try {
      const response = await fetch('/api/analysis/content-strategy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ domain })
      });
      if (!response.ok) throw new Error('Failed');
      const result = await response.json();
      setContentStrategyData(result);
    } catch (err) { console.error('Content strategy error:', err); }
    finally { setContentStrategyLoading(false); }
  };




  const AI_SEARCH_FAQ = [
    { q: "What is AI Search Visibility and why does it matter?", a: "AI Search Visibility measures how prominently your brand and content appear in AI-powered search engines like ChatGPT, Google AI Overviews, Perplexity, and Claude. As more users rely on AI assistants for answers, traditional SEO alone is no longer sufficient. AI Search Visibility ensures your content is structured and optimized to be cited, referenced, and recommended by AI systems, driving a new wave of organic traffic and brand authority." },
    { q: "How does SERPfinity track AI search visibility?", a: "SERPfinity monitors your brand mentions, citations, and content appearances across all major AI search platforms in real-time. Our proprietary tracking technology analyzes AI-generated responses from ChatGPT, Google Gemini, Perplexity AI, Microsoft Copilot, and more. We provide sentiment analysis, citation frequency, competitive positioning, and actionable recommendations to improve your AI search presence." },
    { q: "What is the difference between SEO and AEO (Answer Engine Optimization)?", a: "SEO focuses on ranking web pages in traditional search engine results pages (SERPs). AEO goes beyond rankings by optimizing content to be the direct answer provided by AI assistants and answer engines. AEO involves structured data markup, entity optimization, natural language content formatting, FAQ schemas, and building topical authority so AI systems choose your content as the authoritative source." },
    { q: "How can I improve my brand visibility in AI search results?", a: "Start by implementing structured data and schema markup across your site. Create comprehensive, authoritative content that directly answers user questions. Build strong entity signals through consistent NAP data, Wikipedia references, and knowledge panel optimization. Use SERPfinity to monitor which AI platforms mention your brand, track sentiment trends, and identify content gaps where competitors are being cited instead." },
    { q: "Does AI search visibility impact traditional SEO rankings?", a: "Yes, AI search visibility and traditional SEO are increasingly interconnected. Google AI Overviews pull from the same content that ranks in organic search, but prioritize content with strong entity signals, structured data, and comprehensive topic coverage. Improving your AEO signals also strengthens your E-E-A-T (Experience, Expertise, Authoritativeness, Trustworthiness), which directly impacts traditional rankings." },
    { q: "Which AI search platforms does SERPfinity monitor?", a: "SERPfinity tracks visibility across all major AI search platforms including Google AI Overviews (SGE), ChatGPT (OpenAI), Perplexity AI, Microsoft Copilot (Bing AI), Claude (Anthropic), Google Gemini, Meta AI, and emerging AI search engines. Our platform continuously adds new AI sources as the landscape evolves, ensuring comprehensive visibility monitoring for your brand." }
  ];

    return (
      <>
        <Helmet>
          <title>AI Search Visibility Tracker | Monitor AI Citations & Brand Mentions | SERPfinity</title>
          <meta name="description" content="Track and optimize your brand visibility across AI search engines including ChatGPT, Google AI Overviews, Perplexity, and Claude. Monitor AI citations, sentiment analysis, and competitive positioning with SERPfinity." />
          <meta name="keywords" content="AI search visibility, AEO optimization, answer engine optimization, AI citations tracker, ChatGPT brand monitoring, Google AI Overviews, Perplexity tracking, AI search optimization, brand visibility AI, SERPfinity" />
          <link rel="canonical" href="https://thedigitalhall.club/ai-search-visibility" />
          <meta property="og:title" content="AI Search Visibility Tracker | SERPfinity" />
          <meta property="og:description" content="Monitor and optimize your brand presence across AI search platforms. Track citations, analyze sentiment, and improve visibility in ChatGPT, Google AI, and more." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://thedigitalhall.club/ai-search-visibility" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="AI Search Visibility Tracker | SERPfinity" />
          <meta name="twitter:description" content="Track your brand visibility across AI search engines. Monitor ChatGPT, Google AI Overviews, Perplexity citations and more." />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":AI_SEARCH_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
        </Helmet>
    <div className="min-h-screen bg-white"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <CpuChipIcon className="w-10 h-10 text-blue-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">AI Search Visibility</h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Analyze how visible your brand is across AI-powered search platforms like ChatGPT, Google AI, Perplexity, and more.
          </p>
        </motion.div>

            {savedDomains.length > 0 && (
              <div className="max-w-2xl mx-auto mb-4">
                <label className="block text-sm text-gray-600 mb-2">Select a saved domain or enter a new one below</label>
                <select
                  value={domain}
                  onChange={(e) => { setDomain(e.target.value); setAutoLoaded(false); setSnapshotData(null); setBrandImpactData(null); setBrandSentimentData(null); setNarrativeData(null); setSearchQuestionsData(null); setPromptIntelligenceData(null); setStrategicPlanData(null); setContentStrategyData(null); }}
                  className="w-full py-3 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Choose a domain --</option>
                  {savedDomains.map((d) => (
                    <option key={d.id} value={d.domain}>{d.domain}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="max-w-2xl mx-auto mb-4 p-3 bg-gray-50 border border-gray-300 rounded-xl flex items-center justify-between text-sm">
              <span className="text-gray-700">
                Plan: <span className="text-blue-600 font-semibold capitalize">{userPlan}</span>  Domains: <span className="text-gray-900 font-semibold">{domainCount}/{domainLimit}</span>
              </span>
              {domainCount >= domainLimit && (
                <a href="/pricing" className="text-blue-600 hover:text-orange-300 underline text-xs">Upgrade Plan</a>
              )}
            </div>
            {refreshBlocked && (
              <div className="max-w-2xl mx-auto mb-4 p-3 bg-yellow-50 border border-yellow-400 rounded-xl text-yellow-700 text-sm text-center">
                Weekly refresh limit reached. Next refresh available: {nextRefreshDate ? new Date(nextRefreshDate).toLocaleDateString() : 'in 7 days'}
              </div>
            )}
        <form onSubmit={handleAnalyze} className="flex gap-4 max-w-2xl mx-auto mb-8">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-600" />
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="Enter your domain (e.g., example.com)"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <SparklesIcon className="w-5 h-5" />
            {loading ? 'Analyzing...' : 'Analyze Now'}
          </button>
        </form>

          {/* Refresh Status Banner */}
          {refreshStatus && refreshStatus.mainReport && refreshStatus.mainReport.exists && (
            <div className="max-w-2xl mx-auto mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">Last analyzed:</span>
                  <span>{new Date(refreshStatus.mainReport.lastRefreshedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="text-gray-400">|</span>
                  <span className="capitalize">{refreshStatus.plan} plan</span>
                  <span className="text-gray-400">|</span>
                  <span>Refresh every {refreshStatus.refreshCooldownDays === 0 ? 'on-demand' : refreshStatus.refreshCooldownDays + ' days'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {refreshStatus.mainReport.canRefresh ? (
                    <button
                      type="button"
                      onClick={() => { setAutoLoaded(false); setSnapshotData(null); handleAnalyze({ preventDefault: () => {} }); }}
                      className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                    >
                       Refresh Analysis
                    </button>
                  ) : (
                    <span className="text-xs text-gray-500">
                      Next refresh: {refreshStatus.mainReport.nextRefreshAt ? new Date(refreshStatus.mainReport.nextRefreshAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

        {error && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-50 border border-red-400 rounded-xl text-red-600 text-center">
            {error}
          </div>
        )}

        {(snapshotData || loading) && (
          <div className="mb-6">
            {/* Section Tabs */}
            <div className="flex gap-1 border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveSection('snapshot')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  activeSection === 'snapshot' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                } border-b-2`}
              >
                <CpuChipIcon className="w-4 h-4" /> Visibility Snapshot
              </button>
              <button
                onClick={() => { setActiveSection('brandImpact'); loadBrandImpact(); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  activeSection === 'brandImpact' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                } border-b-2`}
              >
                <ShieldCheckIcon className="w-4 h-4" /> Brand Impact
              </button>
              <button
                onClick={() => { setActiveSection('brandSentiment'); loadBrandSentiment(); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  activeSection === 'brandSentiment' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                } border-b-2`}
              >
                <FaceSmileIcon className="w-4 h-4" /> Brand Sentiment
              </button>
              <button
                onClick={() => { setActiveSection('narrative'); loadNarrativeAnalysis(); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  activeSection === 'narrative' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Narrative Analysis
              </button>
              <button
                onClick={() => { setActiveSection('searchQuestions'); loadSearchQuestions(); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  activeSection === 'searchQuestions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Search Questions
              </button>
            <button
              onClick={() => { setActiveSection('promptIntelligence'); loadPromptIntelligence(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                activeSection === 'promptIntelligence' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Prompt Intelligence
            </button>
            <button
              onClick={() => { setActiveSection('strategicPlan'); loadStrategicPlan(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                activeSection === 'strategicPlan' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Strategic Plan
            </button>
            <button
              onClick={() => { setActiveSection('contentStrategy'); loadContentStrategy(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                activeSection === 'contentStrategy' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Content Strategy
            </button>
            </div>

            {/* Section Content */}
            {activeSection === 'snapshot' && snapshotData && (
              <AISearchVisibility snapshotData={snapshotData} domain={domain} />
            )}

            {activeSection === 'brandImpact' && (
              <>
                {brandImpactLoading && (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600">Analyzing brand impact...</p>
                    <p className="text-gray-500 text-sm">This may take 15-30 seconds</p>
                  </div>
                )}
                {brandImpactData && <BrandImpactAnalysis data={{...brandImpactData, domain}} />}
              </>
            )}

            {activeSection === 'brandSentiment' && (
              <>
                {brandSentimentLoading && (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600">Analyzing brand sentiment...</p>
                    <p className="text-gray-500 text-sm">This may take 15-30 seconds</p>
                  </div>
                )}
                {brandSentimentData && <BrandSentimentAnalysis data={brandSentimentData} />}</>
            )}

            {activeSection === 'narrative' && (
              <div>
                {narrativeLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Analyzing brand narratives...</span>
                  </div>
                )}
                {narrativeData && <NarrativeAnalysis data={narrativeData} />}
              </div>
            )}

            {activeSection === 'searchQuestions' && (
              <div>
                {searchQuestionsLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Analyzing search questions...</span>
                  </div>
                )}
                {searchQuestionsData && <SearchQuestionsAnalysis data={searchQuestionsData} />}
              </div>
            )}

            {activeSection === 'promptIntelligence' && (
              <div>
                {promptIntelligenceLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Analyzing prompt intelligence...</span>
                  </div>
                )}
                {promptIntelligenceData && <PromptIntelligence data={promptIntelligenceData} />}
              </div>
            )}

            {activeSection === 'strategicPlan' && (
              <div>
                {strategicPlanLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Building strategic action plan...</span>
                  </div>
                )}
                {strategicPlanData && <StrategicActionPlan data={strategicPlanData} />}
              </div>
            )}

            {activeSection === 'contentStrategy' && (
              <div>
                {contentStrategyLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Creating content strategy...</span>
                  </div>
                )}
                {contentStrategyData && <ContentStrategy data={contentStrategyData} />}
              </div>
            )}
          </div>
        )}

        {!snapshotData && !loading && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white shadow-lg rounded-2xl p-8 border border-gray-200 text-center">
              <SparklesIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Discover Your AI Visibility</h3>
              <p className="text-gray-600 mb-6">
                Enter your domain above to get a comprehensive visibility snapshot across all major AI search platforms.
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4"/> 14-day free trial</span>
                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4"/> No credit card required</span>
                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4"/> Cancel anytime</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
        <FAQSection items={AI_SEARCH_FAQ} />
      </>
  );
};

export default AISearchVisibilityPage;
